import React from 'react';
import assets from '../assets';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSupplierSurvey } from '../hooks';
import PageLoading from '../../../components/PageLoading';
import SomethingWentWrong from '../../../components/SomethingWentWrong';
import SurveyContainer from '../../../components/Survey/Container';
import { Logo } from '../../../components/Survey/icons';
import ImageHeader from '../../../components/Survey/ImageHeader';
import { Box, Button } from '@mui/material';
import themeColors from 'src/theme/colors';
import FinishedCheckMark from './FinishedCheckMark';
import { registrationTokenParam } from '../utils';

export default function SupplierSurveyMainPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: 'ghg.supplierSurvey' });
  const survey = useSupplierSurvey();
  const location = useLocation();

  const [searchParams, _] = useSearchParams();
  const registrationToken = searchParams.get('registration_token');

  if (survey.isError) return <SomethingWentWrong />;
  if (!survey.data) return <PageLoading />;
  if (!survey.data.answer) {
    return <Navigate to={`../initial-questions${location.search}`} replace />;
  }

  const prevLink = {
    onClick: () => {
      navigate(
        `../initial-questions${registrationTokenParam(registrationToken)}`
      );
    },
    label: 'ghg.supplierSurvey.back',
  };

  const { answer } = survey.data;

  const firstStepFinished =
    (answer.has_sedex_questionnaire &&
      answer.zc_code !== null &&
      answer.saq_file !== null) ||
    (!answer.has_sedex_questionnaire &&
      answer.questionnaire_answer_esg_submitted);

  const secondStepFinished =
    (answer.wants_to_use_platform && survey.data.is_registered) ||
    (!answer.wants_to_use_platform &&
      answer.questionnaire_answer_environment_submitted);

  const nextLink = {
    onClick: () => {
      navigate(`../end`);
    },
    label: 'ghg.supplierSurvey.finish',
  };

  const buttonSx = (stepFinished: boolean) => ({
    height: '180px',
    width: '240px',
    bgcolor: stepFinished ? themeColors.disabledLabel : undefined,
    ':hover': {
      bgcolor: stepFinished ? themeColors.ghgMain : undefined,
    },
  });

  return (
    <SurveyContainer
      logo={<Logo />}
      header={
        <ImageHeader
          imageSrc={assets.questionnaire.image}
          text={t('mainHeader')}
          small
        />
      }
      prevLink={prevLink}
      nextLink={firstStepFinished && secondStepFinished ? nextLink : undefined}
    >
      <Box display="flex" flexDirection="row" justifyContent="space-around">
        <Button
          sx={{
            ...buttonSx(firstStepFinished),
            fontSize: '48px',
          }}
          onClick={() =>
            navigate(
              `../first-step${registrationTokenParam(registrationToken)}`,
              {}
            )
          }
        >
          {firstStepFinished && <FinishedCheckMark />}
          {t('firstStep.title')}
        </Button>
        <Button
          sx={{
            ...buttonSx(secondStepFinished),
            fontSize: '32px',
          }}
          onClick={() =>
            navigate(
              `../second-step${registrationTokenParam(registrationToken)}`
            )
          }
        >
          {secondStepFinished && <FinishedCheckMark />}
          {t('secondStep.title')}
        </Button>
      </Box>
    </SurveyContainer>
  );
}
